import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FilterSection from '../components/FilterSection';
import PatientCard, { calculateAge } from '../components/PatientCard';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getToken } from '../utils/auth';
import Modal, { DialogHeader, DialogFooter, DialogBody } from '../components/ui/Modal';
import PillTabs from '../components/ui/PillTabs';

import { AnimatedNumber } from '../motion';
import {
  Users,
  CircleCheck,
  CircleMinus,
  CircleAlert,
  GripVertical,
  Plus,
  LayoutGrid,
  List,
  Table2,
  ChevronRight,
  X,
} from 'lucide-react';

const PATIENT_ORDER_KEY = 'vitra_myPatients_customOrder';

const MyPatientsPage = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const [viewMode, setViewMode] = useState('card');
  const [showAddPatientForm, setShowAddPatientForm] = useState(false);
  const [isSubmittingPatient, setIsSubmittingPatient] = useState(false);
  const hoverTimeoutRef = useRef(null);

  const [patientOrder, setPatientOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(PATIENT_ORDER_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [draggedId, setDraggedId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  const [feelingCounts, setFeelingCounts] = useState({
    stable: 0,
    moderate: 0,
    unwell: 0
  });
  
  const [filterOptions, setFilterOptions] = useState({
    diagnoses: [],
    clinicians: [],
    genders: []
  });
  
  const [newPatient, setNewPatient] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    gender: '',
    phone: '',
    email: '',
    username:''
  });

  const fetchFeelingCounts = useCallback(async (patientsList) => {
    try {
      const token = getToken();
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const mrns = patientsList.map(p => p.MRN);
      
      const feelingPromises = mrns.map(mrn => 
        axios.get(API_BASE_URL + `/patient-daily-feelings/patient/${mrn}/latest`, { headers })
          .then(res => {
            const feelingValue = res.data?.feelingTag?.toLowerCase();
            return feelingValue;
          })
          .catch(() => null)
      );

      const feelings = await Promise.all(feelingPromises);
      
      const counts = {
        stable: feelings.filter(f => f === 'stable').length,
        moderate: feelings.filter(f => f === 'moderate').length,
        unwell: feelings.filter(f => f === 'unwell').length
      };

      setFeelingCounts(counts);
    } catch (error) {
      console.error('Error fetching feeling counts:', error);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const token = getToken();
        if (!token) {
          console.error('No authentication token found');
          return;
        }
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(API_BASE_URL + '/patients/', { headers});
        setPatients(response.data);
        setFiltered(response.data);
        
        const uniqueDiagnoses = [...new Set(response.data.map(p => p.diagnosis).filter(Boolean))];
        const uniqueClinicians = [...new Set(response.data.map(p => p.clinican).filter(Boolean))];
        const uniqueGenders = [...new Set(response.data.map(p => p.gender).filter(Boolean))];
        
        setFilterOptions({
          diagnoses: uniqueDiagnoses,
          clinicians: uniqueClinicians,
          genders: uniqueGenders
        });

        fetchFeelingCounts(response.data);
      } catch (error) {
        console.error('Error fetching patients:', error);
      }
    };
    fetchPatients();
  }, [API_BASE_URL, fetchFeelingCounts]);

  const patientFilters = [
    { name: 'name', label: 'Patient Name', type: 'text', placeholder: 'Enter patient name' },
    { name: 'MRN', label: 'Patient MRN', type: 'text', placeholder: 'Enter patient MRN' },
    { name: 'diagnosis', label: 'Diagnosis', type: 'select', options: filterOptions.diagnoses },
    { name: 'checkupDate', label: 'Checkup Date', type: 'date' },
    { name: 'age', label: 'Age', type: 'range', min: 1, max: 100, defaultValue: 0 },
    { name: 'gender', label: 'Gender', type: 'radio', options: filterOptions.genders },
  ];

  const handleFilter = (filters) => {
    setActiveFilters(filters);
    const filteredList = patients.filter((p) => {
      const patientAge = calculateAge(p.dob);
      const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
      
      return (
        (!filters.name || fullName.includes(filters.name.toLowerCase())) &&
        (!filters.MRN || p.MRN?.toString().includes(filters.MRN)) &&
        (!filters.diagnosis || p.diagnosis === filters.diagnosis) &&
        (!filters.gender || p.gender === filters.gender) &&
        (!filters.age || (patientAge !== 'N/A' && patientAge >= filters.age)) &&
        (!filters.checkupDate || p.lastCheckUp === filters.checkupDate)
      );
    });
    setFiltered(filteredList);
    fetchFeelingCounts(filteredList);
  };

  const removeFilter = (key) => {
    const updatedFilters = { ...activeFilters };
    delete updatedFilters[key];
    setActiveFilters(updatedFilters);
    const filteredList = patients.filter((p) => {
      const patientAge = calculateAge(p.dob);
      const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
      
      return (
        (!updatedFilters.name || fullName.includes(updatedFilters.name.toLowerCase())) &&
        (!updatedFilters.MRN || p.MRN?.toString().includes(updatedFilters.MRN)) &&
        (!updatedFilters.diagnosis || p.diagnosis === updatedFilters.diagnosis) &&
        (!updatedFilters.gender || p.gender === updatedFilters.gender) &&
        (!updatedFilters.age || (patientAge !== 'N/A' && patientAge >= updatedFilters.age)) &&
        (!updatedFilters.checkupDate || p.lastCheckUp === updatedFilters.checkupDate)
      );
    });
    setFiltered(filteredList);
    fetchFeelingCounts(filteredList);
  };

  const handlePatientClick = (patientId) => {
    navigate(`/patient/${patientId}`);
  };

    

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPatient(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddPatient = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmittingPatient(true);
      const response = await axios.post(`${API_BASE_URL}/patients/`, newPatient);
      
      const updatedPatients = [...patients, response.data];
      setPatients(updatedPatients);
      setFiltered(updatedPatients);
      
      setNewPatient({
        firstName: '',
        lastName: '',
        dob: '',
        gender: '',
        phone: '',
        email: '',
        username:''
      });
      setShowAddPatientForm(false);
      
      alert('Patient added successfully!');
    } catch (error) {
      console.error('Error adding patient:', error);
      alert('Failed to add patient. Please try again.');
    } finally {
      setIsSubmittingPatient(false);
    }
  };

  const closeAddPatientModal = () => {
    if (isSubmittingPatient) return;
    setShowAddPatientForm(false);
  };

  useEffect(() => {
    if (!showAddPatientForm) return undefined;
    const onEsc = (e) => {
      if (e.key === 'Escape' && !isSubmittingPatient) setShowAddPatientForm(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [showAddPatientForm, isSubmittingPatient]);

  const total = filtered.length;

  const getStatusColor = (status) => {
    switch (status) {
      case 'Not active': return 'text-gray-600 bg-gray-100';
      case 'Critical': return 'text-red-600 bg-red-50';
      case 'High Risk': return 'text-yellow-600 bg-yellow-50';
      case 'Stable': return 'text-green-600 bg-green-50';
      case 'Active': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const savePatientOrder = (orderIds) => {
    setPatientOrder(orderIds);
    try {
      localStorage.setItem(PATIENT_ORDER_KEY, JSON.stringify(orderIds));
    } catch (e) {
      console.warn('Could not save patient order:', e);
    }
  };

  const applyCustomOrder = (list) => {
    if (!patientOrder.length) return list;
    const orderMap = new Map(patientOrder.map((id, i) => [String(id), i]));
    return [...list].sort((a, b) => {
      const ai = orderMap.has(String(a.id)) ? orderMap.get(String(a.id)) : Number.MAX_SAFE_INTEGER;
      const bi = orderMap.has(String(b.id)) ? orderMap.get(String(b.id)) : Number.MAX_SAFE_INTEGER;
      if (ai !== bi) return ai - bi;
      return 0;
    });
  };

  const draggedIdRef = useRef(null);
  const dragOverIdRef = useRef(null);
  const didDragRef = useRef(false);
  const pointerStartRef = useRef({ x: 0, y: 0 });
  const filteredRef = useRef(filtered);
  const patientOrderRef = useRef(patientOrder);
  filteredRef.current = filtered;
  patientOrderRef.current = patientOrder;

  const findPatientIdAtPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    const host = el.closest('[data-patient-id]');
    return host ? host.getAttribute('data-patient-id') : null;
  };

  const reorderPatients = useCallback((sourceId, targetId) => {
    if (sourceId == null || targetId == null || String(sourceId) === String(targetId)) return;

    const current = filteredRef.current;
    const order = patientOrderRef.current;

    const ordered = (() => {
      if (!order.length) return [...current];
      const orderMap = new Map(order.map((id, i) => [String(id), i]));
      return [...current].sort((a, b) => {
        const ai = orderMap.has(String(a.id)) ? orderMap.get(String(a.id)) : Number.MAX_SAFE_INTEGER;
        const bi = orderMap.has(String(b.id)) ? orderMap.get(String(b.id)) : Number.MAX_SAFE_INTEGER;
        return ai - bi;
      });
    })();

    const ids = ordered.map((p) => p.id);
    const fromIndex = ids.findIndex((id) => String(id) === String(sourceId));
    const toIndex = ids.findIndex((id) => String(id) === String(targetId));
    if (fromIndex < 0 || toIndex < 0) return;

    const nextIds = [...ids];
    const [moved] = nextIds.splice(fromIndex, 1);
    nextIds.splice(toIndex, 0, moved);

    const filteredSet = new Set(ids.map(String));
    const merged = [
      ...nextIds,
      ...order.filter((id) => !filteredSet.has(String(id))),
    ];
    savePatientOrder(merged);

    const byId = new Map(ordered.map((p) => [String(p.id), p]));
    setFiltered(nextIds.map((id) => byId.get(String(id))).filter(Boolean));
  }, []);

  const endPointerDrag = useCallback((clientX, clientY) => {
    const sourceId = draggedIdRef.current;
    if (sourceId == null) return;

    let targetId = dragOverIdRef.current;
    if ((targetId == null || String(targetId) === String(sourceId)) && clientX != null) {
      targetId = findPatientIdAtPoint(clientX, clientY);
    }

    if (didDragRef.current && targetId != null && String(sourceId) !== String(targetId)) {
      reorderPatients(sourceId, targetId);
    }

    draggedIdRef.current = null;
    dragOverIdRef.current = null;
    setDraggedId(null);
    setDragOverId(null);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }, [reorderPatients]);

  const onItemPointerDown = (e, patientId) => {
    if (e.button != null && e.button !== 0) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    didDragRef.current = false;
    draggedIdRef.current = patientId;
    dragOverIdRef.current = null;
    setDraggedId(patientId);
    setDragOverId(null);
    document.body.style.userSelect = 'none';
  };

  const onItemPointerMove = (e) => {
    if (draggedIdRef.current == null) return;
    const dx = e.clientX - pointerStartRef.current.x;
    const dy = e.clientY - pointerStartRef.current.y;
    if (!didDragRef.current && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      didDragRef.current = true;
      document.body.style.cursor = 'grabbing';
    }
    if (!didDragRef.current) return;

    const overId = findPatientIdAtPoint(e.clientX, e.clientY);
    if (overId != null && String(overId) !== String(draggedIdRef.current)) {
      if (String(overId) !== String(dragOverIdRef.current)) {
        dragOverIdRef.current = overId;
        setDragOverId(overId);
      }
    }
  };

  const onItemPointerUp = (e) => {
    if (draggedIdRef.current == null) return;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch (_) {
    }
    endPointerDrag(e.clientX, e.clientY);
  };

  const handlePatientCardClick = (patientId) => {
    if (didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    handlePatientClick(patientId);
  };

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: null, direction: 'asc' };
    });
  };

  const getSortValue = (patient, key) => {
    switch (key) {
      case 'name':
        return `${patient.firstName || ''} ${patient.lastName || ''}`.toLowerCase();
      case 'MRN':
        return patient.MRN?.toString().toLowerCase() || '';
      case 'age': {
        const age = calculateAge(patient.dob);
        return age === 'N/A' || age == null ? -1 : Number(age);
      }
      case 'gender':
        return (patient.gender || '').toLowerCase();
      case 'diagnosis':
        return (patient.diagnosis || '').toLowerCase();
      case 'clinician':
        return (patient.clinican || '').toLowerCase();
      case 'status':
        return (patient.status || '').toLowerCase();
      case 'lastCheckUp':
        return patient.lastCheckUp ? new Date(patient.lastCheckUp).getTime() || 0 : 0;
      default:
        return '';
    }
  };

  const sortedForTable = (() => {
    const ordered = applyCustomOrder(filtered);
    if (!sortConfig.key) return ordered;
    return [...ordered].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const displayPatients = viewMode === 'table' ? sortedForTable : applyCustomOrder(filtered);

  const SortableHeader = ({ label, sortKey }) => {
    const isActive = sortConfig.key === sortKey;
    return (
      <th
        onClick={() => handleSort(sortKey)}
        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 transition-colors"
        title={`Sort by ${label}`}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          <span className={`normal-case text-[10px] ${isActive ? 'text-teal-600' : 'text-gray-300'}`}>
            {isActive ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '⇅'}
          </span>
        </span>
      </th>
    );
  };

  const DragHandleHint = () => (
    <span
      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-400 bg-gray-50 border border-gray-200 pointer-events-none"
      title="Drag to reorder"
      aria-hidden="true"
    >
      <GripVertical className="w-4 h-4" strokeWidth={2} />
    </span>
  );

  return (
    <div className="min-h-screen flex flex-col vitra-atmosphere">
      <Header />
      <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Patients</h2>

        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg rounded-xl p-6 text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-blue-100 text-sm font-semibold uppercase tracking-wide">Total Patients</p>
                <p className="text-4xl font-bold mt-2 tabular-nums">
                  <AnimatedNumber value={total} />
                </p>
              </div>
              <div className="flex-shrink-0 bg-white/20 rounded-full p-3 text-white">
                <Users className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-green-500 to-green-600 shadow-lg rounded-xl p-6 text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-green-100 text-sm font-semibold uppercase tracking-wide">Stable</p>
                <p className="text-4xl font-bold mt-2 tabular-nums">
                  <AnimatedNumber value={feelingCounts.stable} />
                </p>
              </div>
              <div className="flex-shrink-0 bg-white/20 rounded-full p-3 text-white">
                <CircleCheck className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-yellow-500 to-yellow-600 shadow-lg rounded-xl p-6 text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-yellow-100 text-sm font-semibold uppercase tracking-wide">Moderate</p>
                <p className="text-4xl font-bold mt-2 tabular-nums">
                  <AnimatedNumber value={feelingCounts.moderate} />
                </p>
              </div>
              <div className="flex-shrink-0 bg-white/20 rounded-full p-3 text-white">
                <CircleMinus className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />
              </div>
            </div>
          </div>

          <div className="relative overflow-hidden bg-gradient-to-br from-red-500 to-red-600 shadow-lg rounded-xl p-6 text-white transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-xl">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-red-100 text-sm font-semibold uppercase tracking-wide">Unwell</p>
                <p className="text-4xl font-bold mt-2 tabular-nums">
                  <AnimatedNumber value={feelingCounts.unwell} />
                </p>
              </div>
              <div className="flex-shrink-0 bg-white/20 rounded-full p-3 text-white">
                <CircleAlert className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />
              </div>
            </div>
          </div>
        </div>

        
        <FilterSection fields={patientFilters} onFilter={handleFilter} />

        
        {Object.entries(activeFilters).filter(([key, value]) => value).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {Object.entries(activeFilters).map(([key, value]) => (
              value && (
                <div key={key} className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full flex items-center text-sm">
                  <span>{key}: {value}</span>
                  <button
                    className="ml-2 text-teal-800 hover:text-teal-900"
                    onClick={() => removeFilter(key)}
                    aria-label={`Remove ${key} filter`}
                  >
                    <X className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </button>
                </div>
              )
            ))}
          </div>
        )}

        <button
            onClick={() => setShowAddPatientForm(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 py-2.5 rounded-lg hover:from-teal-600 hover:to-teal-700 shadow-md hover:shadow-lg transition-all duration-200 font-medium"
          >
            <Plus className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
            Add New Patient
          </button>

        
        <div className="flex justify-between items-center mb-4 gap-2">
          {(viewMode === 'card' || viewMode === 'list') && (
            <p className="text-xs text-gray-500">
              Click and drag any card/row to reorder. Your order is saved automatically.
            </p>
          )}
          {viewMode === 'table' && <span />}
          <div className="ml-auto">
            <PillTabs
              value={viewMode}
              onChange={setViewMode}
              layoutId="patients-view-mode"
              size="sm"
              options={[
                { id: 'card', label: 'Cards', icon: LayoutGrid },
                { id: 'list', label: 'List', icon: List },
                { id: 'table', label: 'Table', icon: Table2 },
              ]}
            />
          </div>
        </div>

        
        <Modal
          open={showAddPatientForm}
          onClose={closeAddPatientModal}
          align="start"
        >
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 max-w-2xl w-full max-h-[calc(100vh-6rem)] sm:max-h-[calc(100vh-8rem)] overflow-hidden flex flex-col">
              <DialogHeader
                title="Add New Patient"
                subtitle="Enter core demographics and contact details."
                onClose={() => { if (!isSubmittingPatient) closeAddPatientModal(); }}
                tone="teal"
                sticky
                className={isSubmittingPatient ? '[&_button]:opacity-50 [&_button]:pointer-events-none' : ''}
              />

              <form onSubmit={handleAddPatient} className="flex flex-col flex-1 min-h-0 overflow-hidden">
                <DialogBody className="px-6 py-6 sm:px-7 sm:py-7">
                <div className="mb-7">
                  <p className="text-sm text-gray-600 bg-blue-50 border-l-4 border-blue-400 p-3 rounded">
                    <span className="font-semibold"
                          >If username not given, mentioned email will be considered as username.</span>
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      First Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={newPatient.firstName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter first name"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Last Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={newPatient.lastName}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter last name"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="gender"
                      value={newPatient.gender}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Date of Birth <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="dob"
                      value={newPatient.dob}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={newPatient.phone}
                      onChange={handleInputChange}
                      placeholder="Contact number"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span  className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={newPatient.email}
                      onChange={handleInputChange}
                      placeholder="patient@example.com"
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>

                  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
                  <input
                    type="text"
                    name="username"
                    value={newPatient.username}
                    onChange={handleInputChange}
                    placeholder="username for login"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>

                </div>
                </DialogBody>

                <DialogFooter>
                  <button
                    type="button"
                    onClick={closeAddPatientModal}
                    disabled={isSubmittingPatient}
                    className="px-6 py-2.5 border-2 border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingPatient}
                    className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg hover:from-teal-600 hover:to-teal-700 font-medium shadow-md hover:shadow-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmittingPatient ? 'Adding...' : 'Add Patient'}
                  </button>
                </DialogFooter>
              </form>
            </div>
        </Modal>

        
        {viewMode === 'card' && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 min-h-[300px]">
            {displayPatients.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-3 col-span-full">
                <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-gray-600 font-medium">Loading patients...</p>
              </div>
            ) : (
              displayPatients.map((patient) => (
                <div
                  key={patient.id}
                  data-patient-id={patient.id}
                  onPointerDown={(e) => onItemPointerDown(e, patient.id)}
                  onPointerMove={onItemPointerMove}
                  onPointerUp={onItemPointerUp}
                  onPointerCancel={onItemPointerUp}
                  onClick={() => handlePatientCardClick(patient.id)}
                  className={`touch-none select-none cursor-grab active:cursor-grabbing rounded-lg ${
                    dragOverId != null && String(dragOverId) === String(patient.id) && String(draggedId) !== String(patient.id)
                      ? 'ring-2 ring-teal-400 ring-offset-2 scale-[1.02]'
                      : ''
                  } ${draggedId != null && String(draggedId) === String(patient.id) ? 'opacity-40' : ''}`}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="flex items-center gap-2 mb-1 px-1 text-xs text-gray-400">
                    <DragHandleHint />
                    <span>Drag to reorder</span>
                  </div>
                  <PatientCard patient={patient} />
                </div>
              ))
            )}
          </div>
        )}

        
        {viewMode === 'list' && (
          <div className="mt-6 space-y-3 min-h-[300px]">
            {displayPatients.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-3 col-span-full">
                <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-gray-600 font-medium">Loading patients...</p>
              </div>
            ) : (
              displayPatients.map((patient) => {
                const age = calculateAge(patient.dob);
                return (
                  <div
                    key={patient.id}
                    data-patient-id={patient.id}
                    onPointerDown={(e) => onItemPointerDown(e, patient.id)}
                    onPointerMove={onItemPointerMove}
                    onPointerUp={onItemPointerUp}
                    onPointerCancel={onItemPointerUp}
                    onClick={() => handlePatientCardClick(patient.id)}
                    onMouseLeave={handleMouseLeave}
                    className={`bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow touch-none select-none cursor-grab active:cursor-grabbing border-l-4 border-teal-500 ${
                      dragOverId != null && String(dragOverId) === String(patient.id) && String(draggedId) !== String(patient.id)
                        ? 'ring-2 ring-teal-400 scale-[1.01]'
                        : ''
                    } ${draggedId != null && String(draggedId) === String(patient.id) ? 'opacity-40' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3 flex-1">
                        <DragHandleHint />
                        <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                          <span className="text-teal-700 font-bold text-lg">
                            {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-gray-800">{patient.firstName} {patient.lastName}</h3>
                          <p className="text-sm text-gray-500">MRN: {patient.MRN} | Age: {age} | {patient.gender}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">{patient.clinican}</p>
                          <p className="text-xs text-gray-500">{patient.diagnosis}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" strokeWidth={2} aria-hidden="true" />
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        
        {viewMode === 'table' && (
          <div className="mt-6 bg-white rounded-lg shadow overflow-hidden min-h-[300px]">
            {displayPatients.length === 0 ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-3 col-span-full">
                <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin"></div>
                <p className="text-gray-600 font-medium">Loading patients...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <SortableHeader label="Patient" sortKey="name" />
                      <SortableHeader label="MRN" sortKey="MRN" />
                      <SortableHeader label="Age" sortKey="age" />
                      <SortableHeader label="Gender" sortKey="gender" />
                      <SortableHeader label="Diagnosis" sortKey="diagnosis" />
                      <SortableHeader label="Clinician" sortKey="clinician" />
                      <SortableHeader label="Status" sortKey="status" />
                      <SortableHeader label="Last Check-up" sortKey="lastCheckUp" />
                      <th className="px-6 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayPatients.map((patient) => {
                      const age = calculateAge(patient.dob);
                      return (
                        <tr
                          key={patient.id}
                          onClick={() => handlePatientClick(patient.id)}
                          onMouseLeave={handleMouseLeave}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mr-3">
                                <span className="text-teal-700 font-medium">
                                  {patient.firstName?.charAt(0)}{patient.lastName?.charAt(0)}
                                </span>
                              </div>
                              <div className="font-medium text-gray-900">{patient.firstName} {patient.lastName}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.MRN}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{age}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.gender}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{patient.diagnosis}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.clinican}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(patient.status)}`}>
                              {patient.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{patient.lastCheckUp}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <ChevronRight className="w-5 h-5 text-gray-400" strokeWidth={2} aria-hidden="true" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

      </main>
      <Footer />
    </div>
  );
};

export default MyPatientsPage;
